import React from 'react';
import styled from 'styled-components';
import * as Ant from 'antd';
import qs from 'query-string';
import {useOutlet} from 'reconnect.js';
import {Helmet} from 'react-helmet';
import {InfoCircleFilled} from '@ant-design/icons';
import * as JStorage from 'rev.sdk.js/Actions/JStorage';
import * as User from 'rev.sdk.js/Actions/User';
import withPageEntry from '../../withPageEntry';
import * as AppActions from '../../AppActions';
import OrderPaymentCodePreview from '../PrintPreview/OrderPaymentCodePreview';

function PaymentCodePrintPreview(props) {
  const [user] = useOutlet('user');
  const params = qs.parse(props.location.search);
  const [order, setOrder] = React.useState(null);
  const [isFetching, setIsFetching] = React.useState(false);

  const handlePrint = React.useCallback(() => {
    window.print();
  }, []);

  const getStoredToken = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem('token');
    } catch (err) {
      console.warn('failed to read token from localStorage', err);
      return null;
    }
  }, []);

  const token = (user && !user.tmp && user.token) || getStoredToken();

  const fetchRecordByOrderNumber = React.useCallback(
    async (order_number) => {
      setIsFetching(true);
      AppActions.setLoading(true);
      try {
        let checkout, order;

        checkout = await JStorage.fetchOneDocument(
          'checkout',
          {order_number},
          null,
          {token},
        );

        if (checkout.order_id) {
          order = await JStorage.fetchOneDocument(
            'order',
            {order_number},
            null,
            {token},
          );
        }

        setOrder({
          ...checkout,
          ...order,
          id: checkout.id,
          __order_id: order?.id,
        });
      } catch (err) {
        console.warn(err);
        Ant.message.error('發生錯誤');
      } finally {
        setIsFetching(false);
        AppActions.setLoading(false);
      }
    },
    [token],
  );

  React.useEffect(() => {
    if (token && params.order_number) {
      fetchRecordByOrderNumber(params.order_number);
    }
  }, [token, params.order_number, fetchRecordByOrderNumber]);

  if (!params.order_number) {
    return null;
  }

  if (!token) {
    return <LoginSection />;
  }

  return (
    <Wrapper>
      <Helmet>
        <title>
          {order
            ? `台北律師公會繳費條碼${
                order.order_id || `${order.order_number}*`
              }`
            : '台北律師公會繳費條碼'}
        </title>
      </Helmet>
      <div className="toolbar no-print">
        <Ant.Button type="primary" onClick={handlePrint}>
          另存為 PDF
        </Ant.Button>
      </div>
      <div className="page">
        {isFetching ? (
          <div className="loading-box">
            <Ant.Spin tip="繳費條碼資料載入中..." />
          </div>
        ) : (
          <OrderPaymentCodePreview record={order} />
        )}
      </div>
    </Wrapper>
  );
}

function LoginSection() {
  async function onFinish(values) {
    const {username, password} = values;
    AppActions.setLoading(true);
    try {
      await User.login({username, password});
    } catch (ex) {
      console.warn(ex);
      Ant.message.error('無法登入，請確認帳號密碼。');
    }
    AppActions.setLoading(false);
  }

  return (
    <LoginWrapper>
      <Ant.Alert
        className="notice-message"
        type="info"
        showIcon
        message="為保障個資，首次查看繳費條碼請先登入。"
      />
      <h2>會員登入</h2>
      <div className="hint">
        <div className="text">
          <InfoCircleFilled style={{marginRight: 6}} />
          帳號請輸入您的身分證字號，第一個字母為大寫。
        </div>
        <div className="text">
          <InfoCircleFilled style={{marginRight: 6}} />
          若為舊系統用戶第一次登入，密碼預設為您的生日（年份使用西元年，例如:
          19850710）。
        </div>
      </div>
      <Ant.Form onFinish={onFinish} style={{maxWidth: 320, width: '100%'}}>
        <Ant.Form.Item
          name="username"
          rules={[{required: true, message: '帳號為必填欄位'}]}>
          <Ant.Input addonBefore="帳號" size="large" placeholder="請輸入帳號" />
        </Ant.Form.Item>
        <Ant.Form.Item
          name="password"
          rules={[{required: true, message: '密碼為必填欄位'}]}>
          <Ant.Input.Password
            addonBefore="密碼"
            size="large"
            placeholder="請輸入密碼"
          />
        </Ant.Form.Item>
        <Ant.Form.Item>
          <Ant.Button
            type="primary"
            htmlType="submit"
            size="large"
            className="submit">
            登入
          </Ant.Button>
        </Ant.Form.Item>
      </Ant.Form>
    </LoginWrapper>
  );
}

const Wrapper = styled.div`
  border: 1px solid #fafafa;
  background-color: #fafafa;
  margin: 0;
  padding: 0;

  & > .toolbar {
    max-width: 210mm;
    margin: 10px auto 0;
    text-align: right;
  }

  & > .page {
    width: 210mm;
    min-height: 297mm;
    padding: 20mm;
    margin: 10mm auto;
    border: 1px #d3d3d3 solid;
    border-radius: 5px;
    background: white;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);

    .loading-box {
      min-height: 220px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  @page {
    size: A4 portrait;
    margin: 0 auto;
  }

  @media print {
    .no-print {
      display: none !important;
    }

    html,
    body {
      width: 210mm;
      height: 297mm;
    }

    .page {
      margin: 0;
      border: initial;
      border-radius: initial;
      width: initial;
      min-height: initial;
      box-shadow: initial;
      background: initial;
      page-break-after: always;

      :last-child {
        page-break-after: avoid;
      }
    }
  }
`;

const LoginWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #fafafa;

  & > h2 {
    margin-bottom: 8px;
    font-size: 1.5rem;
    color: #b49664;
  }

  & > .notice-message {
    margin-bottom: 20px;
    max-width: 320px;
    width: calc(100% - 40px);
  }

  & > .notice-message .ant-alert-message {
    font-size: 0.95rem;
    font-weight: 400;
    color: #4e4e4e;
  }

  & > .hint {
    margin-bottom: 15px;
    max-width: 320px;
    width: 100%;
    padding: 0px 10px;

    & > .text {
      font-size: 0.95rem;
      color: #868e96;
    }
  }

  .ant-input-group-addon {
    color: #969696;
  }

  .submit {
    background-color: #323232;
    width: 100%;
    color: #b49664;
  }
`;

export default withPageEntry(PaymentCodePrintPreview);
